/**
 * Enterprise Onboarding Setup Flow
 *
 * Light setup shell (matches signup/app) with 4-step activation (UX-01):
 * 1. Confirm organization (from signup data)
 * 2. Create first workspace
 * 3. Starter — guided sample project or golden-template setup
 * 4. Invite teammates (only if workspace created)
 *
 * Every step is skippable. Skip from workspace (or later) lands in the app;
 * skip from starter still offers invite.
 *
 * Placement: starter sits after workspace / before invite because
 * instantiate-v5_1 requires an active workspace, and invites should join
 * a prepared (non-empty) workspace when the admin chose a starter.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2,
  Layers,
  Users,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  Sparkles,
  LayoutTemplate,
} from "lucide-react";
import { createWorkspace } from "@/features/workspaces/api";
import { useWorkspaceStore } from "@/state/workspace.store";
import { finalizeAdminOnboardingOnServer } from "@/features/organizations/finalizeAdminOnboarding";
import { completeOnboardingStep, type OnboardingStepId } from "@/features/organizations/onboarding.api";
import { useAuth } from "@/state/AuthContext";
import { apiClient } from "@/lib/api/client";
import { request } from "@/lib/api";
import { administrationApi } from "@/features/administration/api/administration.api";
import {
  listTemplates,
  instantiateTemplate,
  type TemplateDto,
} from "@/features/templates/templates.api";
import { getEmailDomain, validateInviteEmails, offDomainErrorMessage, INVITE_DOMAIN_HELPER } from "@/utils/invite-validation";

type Step = "org" | "workspace" | "starter" | "invite";

/** Same SYSTEM template DEMO-SEED-1 uses — chosen explicitly here (no auto-seed). */
const SAMPLE_TEMPLATE_CODE = "sw_release_planning_v1";
const SAMPLE_PROJECT_NAME = "[Sample] Release Planning";

type StarterChoice =
  | { kind: "sample" }
  | { kind: "template"; templateId: string; templateName: string };

const STEPS: { id: Step; label: string; icon: typeof Building2 }[] = [
  { id: "org", label: "Organization", icon: Building2 },
  { id: "workspace", label: "Workspace", icon: Layers },
  { id: "starter", label: "Starter", icon: Sparkles },
  { id: "invite", label: "Team", icon: Users },
];

function slugify(v: string) {
  return v.toLowerCase().trim().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

function parseEmails(raw: string): string[] {
  return [...new Set(raw.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { setActiveWorkspace } = useWorkspaceStore();

  const [step, setStep] = useState<Step>("org");
  const [submitting, setSubmitting] = useState(false);

  // Org data
  const [orgName, setOrgName] = useState("");
  const [orgSize, setOrgSize] = useState("");
  const [orgLoading, setOrgLoading] = useState(true);

  // Workspace data
  const [wsName, setWsName] = useState("");
  const [wsDescription, setWsDescription] = useState("");
  const [createdWsId, setCreatedWsId] = useState<string | null>(null);

  // Invite data
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteRole, setInviteRole] = useState<"Member" | "Guest">("Member");

  // Starter (UX-01) data
  const [starterChoice, setStarterChoice] = useState<StarterChoice | null>(null);
  const [starterTemplates, setStarterTemplates] = useState<TemplateDto[]>([]);
  const [starterLoading, setStarterLoading] = useState(false);
  const [starterLoadError, setStarterLoadError] = useState<string | null>(null);

  const emailDomain = getEmailDomain(user?.email);
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  // Load org data on mount (overview returns { profile: { name, ... } }, not top-level name)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get<{
          profile?: { name?: string; size?: string; teamSize?: string };
          data?: { profile?: { name?: string } };
        }>("/admin/organization/overview");
        const payload = (res as any)?.data ?? res;
        const profile = payload?.profile ?? (res as any)?.profile;
        setOrgName(profile?.name?.trim() || "");
        setOrgSize(profile?.size || profile?.teamSize || "");
      } catch {
        setOrgName(emailDomain ? emailDomain.split(".")[0] : "Your organization");
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [emailDomain]);

  // Finalize and exit onboarding
  /**
   * Record a wizard step that actually SUCCEEDED.
   *
   * Best-effort by design: a lost breadcrumb must never fail the step the user
   * just completed. Callers MUST await this before finishAndLand() — never in
   * parallel. `organizations.settings` is a whole-object JSONB read-modify-write
   * with no locking, so complete-step and complete/skip racing each other means
   * one silently clobbers the other.
   */
  async function recordStep(step: OnboardingStepId) {
    try {
      await completeOnboardingStep(step);
    } catch {
      // Breadcrumb only — the checklist degrades, the wizard does not.
    }
  }

  async function finishAndLand(outcome: "completed" | "skipped" = "completed") {
    await finalizeAdminOnboardingOnServer(outcome);
    await qc.invalidateQueries({ queryKey: ["org-onboarding-status"] });
    navigate("/inbox", { replace: true });
  }

  // Load starter templates when entering the starter step
  useEffect(() => {
    if (step !== "starter") return;
    let cancelled = false;
    (async () => {
      setStarterLoading(true);
      setStarterLoadError(null);
      try {
        const all = await listTemplates();
        if (cancelled) return;
        const usable = all.filter(
          (t) =>
            !t.comingSoon &&
            t.isActive !== false &&
            (t.kind === "project" || t.kind === "mixed" || !t.kind),
        );
        // Preferred first, then SYSTEM, then name
        usable.sort((a, b) => {
          const pref = Number(Boolean(b.isPreferred)) - Number(Boolean(a.isPreferred));
          if (pref !== 0) return pref;
          const sys = Number(Boolean(b.isSystem)) - Number(Boolean(a.isSystem));
          if (sys !== 0) return sys;
          return (a.name || "").localeCompare(b.name || "");
        });
        setStarterTemplates(usable);
      } catch (e: any) {
        if (!cancelled) {
          setStarterLoadError(e?.message || "Could not load templates.");
          setStarterTemplates([]);
        }
      } finally {
        if (!cancelled) setStarterLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step]);

  const sampleTemplate = useMemo(
    () => starterTemplates.find((t) => t.templateCode === SAMPLE_TEMPLATE_CODE) ?? null,
    [starterTemplates],
  );

  const goldenTemplates = useMemo(
    () =>
      starterTemplates
        .filter((t) => t.templateCode !== SAMPLE_TEMPLATE_CODE)
        .filter((t) => t.isPreferred || t.isSystem)
        .slice(0, 6),
    [starterTemplates],
  );

  // Skip current step
  async function handleSkip() {
    if (step === "org") {
      setStep("workspace");
    } else if (step === "workspace") {
      // Skip workspace → no starter/invite without a workspace.
      // Must finalize as "skipped": with no workspace, `skipped` is the ONLY
      // field that can clear mustOnboard. Finalizing as "completed" here is what
      // made skip a retrap loop.
      await finishAndLand("skipped");
    } else if (step === "starter") {
      setStep("invite");
    } else {
      await finishAndLand();
    }
  }

  // Step 1: Save org name (admin) then continue
  async function handleOrgContinue() {
    const name = orgName.trim();
    if (!name) {
      toast.error("Enter an organization name.");
      return;
    }
    setSubmitting(true);
    try {
      await request.patch("/admin/organization/profile", { name });
      await recordStep("org");
      setStep("workspace");
    } catch (e: any) {
      toast.error(e?.message || "Could not save organization name.");
    } finally {
      setSubmitting(false);
    }
  }

  // Step 2: Create workspace
  async function handleCreateWorkspace() {
    if (!wsName.trim()) return;
    setSubmitting(true);
    try {
      const ws = await createWorkspace({
        name: wsName.trim(),
        slug: slugify(wsName),
        description: wsDescription.trim() || undefined,
      });
      const id = (ws as any)?.id || (ws as any)?.data?.id;
      if (id) {
        setCreatedWsId(id);
        setActiveWorkspace(id);
      }
      await recordStep("workspace");
      toast.success("Workspace created");
      setStarterChoice(null);
      setStep("starter");
    } catch (e: any) {
      toast.error(e?.message || "Failed to create workspace");
    } finally {
      setSubmitting(false);
    }
  }

  // Step 3 (UX-01): Create sample or golden-template project, then invite
  async function handleStarterContinue() {
    if (!starterChoice) {
      toast.error("Choose a sample project or a template, or skip.");
      return;
    }
    if (!createdWsId) {
      toast.error("Create a workspace before adding a starter project.");
      return;
    }

    setSubmitting(true);
    try {
      if (starterChoice.kind === "sample") {
        const tpl = sampleTemplate;
        if (!tpl?.id) {
          toast.error("Sample template is not available yet. Pick another template or skip.");
          return;
        }
        await instantiateTemplate(tpl.id, { projectName: SAMPLE_PROJECT_NAME });
        toast.success("Sample project ready");
      } else {
        await instantiateTemplate(starterChoice.templateId, {
          projectName: starterChoice.templateName,
        });
        toast.success("Project created from template");
      }
      await recordStep("starter");
      setStep("invite");
    } catch (e: any) {
      toast.error(e?.message || "Failed to create starter project");
    } finally {
      setSubmitting(false);
    }
  }

  // Step 4: Send invites (with same-domain validation)
  async function handleSendInvites() {
    const emails = parseEmails(inviteEmails);
    if (emails.length === 0) return;

    // Enforce same-company domain rule
    if (emailDomain) {
      const { offDomain } = validateInviteEmails(emails, emailDomain);
      if (offDomain.length > 0) {
        toast.error(offDomainErrorMessage(offDomain, emailDomain));
        return;
      }
    }

    setSubmitting(true);
    try {
      await administrationApi.inviteUsers({
        emails,
        platformRole: inviteRole === "Guest" ? "Viewer" : "Member",
        workspaceAssignments: createdWsId
          ? [{ workspaceId: createdWsId, accessLevel: inviteRole === "Guest" ? "Viewer" : "Member" }]
          : [],
      });
      toast.success(`${emails.length} invite${emails.length > 1 ? "s" : ""} sent`);
      // Sequential, never parallel — see recordStep().
      await recordStep("invite");
      await finishAndLand();
    } catch (e: any) {
      toast.error(e?.message || "Failed to send invites");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-auto bg-slate-50" data-testid="onboarding-page">
      {/* Zephix logo */}
      <div className="fixed left-6 top-6 flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-teal-500 text-xs font-bold text-white">
          Z
        </div>
        <span className="text-sm font-semibold text-slate-900">Zephix</span>
      </div>

      {/* Main setup panel */}
      <div className="m-4 w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
        {/* Step progress rail */}
        <div className="flex items-center gap-1 border-b border-slate-200 px-8 py-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isCurrent = s.id === step;
            const isDone = i < stepIndex;
            return (
              <div key={s.id} className="flex items-center gap-1">
                {i > 0 && <div className={`mx-2 h-px w-8 ${isDone ? "bg-emerald-500" : "bg-slate-200"}`} />}
                <div
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    isCurrent
                      ? "bg-blue-50 text-blue-700"
                      : isDone
                        ? "text-emerald-600"
                        : "text-slate-500"
                  }`}
                >
                  {isDone ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="flex min-h-[380px] flex-col md:flex-row">
          {/* Left: context */}
          <div className="flex flex-col justify-center border-b border-slate-200 p-8 md:w-[280px] md:border-b-0 md:border-r">
            {step === "org" && (
              <>
                <h1 className="text-xl font-bold text-slate-900">Set up your organization</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Your organization was created from your signup. Confirm the name (or edit it), then continue.
                </p>
              </>
            )}
            {step === "workspace" && (
              <>
                <h1 className="text-xl font-bold text-slate-900">Create your first workspace</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Workspaces organize projects, people, dashboards, and templates.
                </p>
              </>
            )}
            {step === "starter" && (
              <>
                <h1 className="text-xl font-bold text-slate-900">Start with real work</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Load a guided sample project or create one from a golden template — so the
                  workspace is not empty when you land.
                </p>
              </>
            )}
            {step === "invite" && (
              <>
                <h1 className="text-xl font-bold text-slate-900">Invite your team</h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Invite teammates to start collaborating in your workspace.
                </p>
              </>
            )}
          </div>

          {/* Right: form */}
          <div className="flex flex-1 flex-col justify-center p-8">
            {step === "org" && (
              <OrgStep
                orgName={orgName}
                onOrgNameChange={setOrgName}
                orgSize={orgSize}
                email={user?.email || ""}
                role="Admin"
                loading={orgLoading}
              />
            )}
            {step === "workspace" && (
              <WorkspaceStep
                name={wsName}
                onNameChange={setWsName}
                description={wsDescription}
                onDescriptionChange={setWsDescription}
              />
            )}
            {step === "starter" && (
              <StarterStep
                loading={starterLoading}
                error={starterLoadError}
                sampleTemplate={sampleTemplate}
                goldenTemplates={goldenTemplates}
                choice={starterChoice}
                onChoiceChange={setStarterChoice}
              />
            )}
            {step === "invite" && (
              <InviteStep
                emails={inviteEmails}
                onEmailsChange={setInviteEmails}
                role={inviteRole}
                onRoleChange={setInviteRole}
                domain={emailDomain}
              />
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t border-slate-200 px-8 py-4">
          {step === "workspace" ? (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50"
            >
              Skip setup, go to app
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
            >
              Skip for now
            </button>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-500">
            {step === "org" && "You can also change organization details anytime in settings."}
            {step === "workspace" && "Exit setup now — unfinished steps stay available from Home."}
            {step === "starter" && "Skip leaves the workspace empty — Templates is always available later."}
            {step === "invite" && INVITE_DOMAIN_HELPER}
          </div>

          {step === "org" && (
            <button
              type="button"
              onClick={() => void handleOrgContinue()}
              disabled={submitting || !orgName.trim()}
              className="flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            </button>
          )}
          {step === "workspace" && (
            <button
              type="button"
              onClick={handleCreateWorkspace}
              disabled={!wsName.trim() || submitting}
              className="flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
              Create workspace
            </button>
          )}
          {step === "starter" && (
            <button
              type="button"
              onClick={() => void handleStarterContinue()}
              disabled={!starterChoice || submitting}
              className="flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Create project
            </button>
          )}
          {step === "invite" && (
            <button
              type="button"
              onClick={handleSendInvites}
              disabled={parseEmails(inviteEmails).length === 0 || submitting}
              className="flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send invites
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Step sub-components ── */

function OrgStep({
  orgName,
  onOrgNameChange,
  orgSize,
  email,
  role,
  loading,
}: {
  orgName: string;
  onOrgNameChange: (v: string) => void;
  orgSize: string;
  email: string;
  role: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-600">Organization name</label>
        <input
          type="text"
          value={orgName}
          onChange={(e) => onOrgNameChange(e.target.value)}
          placeholder="e.g. Zephix LLC"
          autoFocus
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <InfoRow label="Your role" value={role} />
      {orgSize ? <InfoRow label="Team size" value={orgSize} /> : null}
      <InfoRow label="Work email" value={email || "—"} />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-600">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

function WorkspaceStep({
  name,
  onNameChange,
  description,
  onDescriptionChange,
}: {
  name: string;
  onNameChange: (v: string) => void;
  description: string;
  onDescriptionChange: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-600">Workspace name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Engineering, Cloud Team, PMO"
          autoFocus
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-600">
          Description <span className="text-slate-600">(optional)</span>
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="What does this workspace focus on?"
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

function StarterStep({
  loading,
  error,
  sampleTemplate,
  goldenTemplates,
  choice,
  onChoiceChange,
}: {
  loading: boolean;
  error: string | null;
  sampleTemplate: TemplateDto | null;
  goldenTemplates: TemplateDto[];
  choice: StarterChoice | null;
  onChoiceChange: (c: StarterChoice) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="onboarding-starter-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-starter-step">
      {error ? (
        <p className="text-sm text-amber-700" data-testid="onboarding-starter-error">
          {error} You can skip and add a project from Templates later.
        </p>
      ) : null}

      <button
        type="button"
        disabled={!sampleTemplate}
        data-testid="onboarding-starter-sample"
        onClick={() => onChoiceChange({ kind: "sample" })}
        className={`w-full rounded-lg border px-4 py-3 text-left transition ${
          choice?.kind === "sample"
            ? "border-blue-500 bg-blue-50"
            : "border-slate-200 bg-white hover:border-slate-300"
        } disabled:cursor-not-allowed disabled:opacity-40`}
      >
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Explore a sample project</div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">
              Guided release-planning project with a real gate — see how work looks before you
              invite anyone.
            </p>
            {!sampleTemplate ? (
              <p className="mt-1 text-xs text-slate-500">Sample template unavailable in this environment.</p>
            ) : null}
          </div>
        </div>
      </button>

      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-600">
          <LayoutTemplate className="h-3.5 w-3.5" />
          Or start from a golden template
        </div>
        {goldenTemplates.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="onboarding-starter-empty-templates">
            No templates available yet. Skip and open Templates after setup.
          </p>
        ) : (
          <ul className="max-h-48 space-y-2 overflow-y-auto pr-1">
            {goldenTemplates.map((t) => {
              const selected =
                choice?.kind === "template" && choice.templateId === t.id;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    data-testid={`onboarding-starter-template-${t.id}`}
                    onClick={() =>
                      onChoiceChange({
                        kind: "template",
                        templateId: t.id,
                        templateName: t.name,
                      })
                    }
                    className={`w-full rounded-lg border px-4 py-2.5 text-left transition ${
                      selected
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-900">{t.name}</div>
                    {t.description ? (
                      <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">{t.description}</p>
                    ) : null}
                    {t.isPreferred ? (
                      <span className="mt-1 inline-block text-[10px] font-medium uppercase tracking-wide text-blue-600/90">
                        Preferred
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function InviteStep({
  emails,
  onEmailsChange,
  role,
  onRoleChange,
  domain,
}: {
  emails: string;
  onEmailsChange: (v: string) => void;
  role: "Member" | "Guest";
  onRoleChange: (v: "Member" | "Guest") => void;
  domain: string;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-600">Team email addresses</label>
        <textarea
          value={emails}
          onChange={(e) => onEmailsChange(e.target.value)}
          placeholder={`colleague@${domain}\nanother@${domain}`}
          rows={3}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />
        <p className="mt-1.5 text-xs text-slate-500">
          Separate multiple emails with commas or new lines.
        </p>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-slate-600">Role</label>
        <select
          value={role}
          onChange={(e) => onRoleChange(e.target.value as "Member" | "Guest")}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="Member">Member — can create and edit work</option>
          <option value="Guest">Guest — read-only access</option>
        </select>
      </div>
    </div>
  );
}
